import * as React from 'react';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Divider from '@mui/material/Divider';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import { useTranslation } from 'react-i18next';
import { Moment } from 'moment-timezone';
import * as Device from 'react-device-detect';

import { BookingOccasionEnum } from '@graphql';

interface ConfirmDialogProps {
  handleConfirm: () => void;
  disabled: boolean;
  startTime: Moment;
  participants: number;
  occasion: BookingOccasionEnum | null;
}

export default function ConfirmDialog(props: Readonly<ConfirmDialogProps>) {
  const { handleConfirm, disabled, startTime, participants, occasion } = props;
  const [open, setOpen] = React.useState(false);
  const { t } = useTranslation('cta');

  const handleClickConfirm = () => {
    setOpen(false);
    handleConfirm();
  };

  const handleOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  return (
    <>
      <Button disabled={disabled} color="success" variant="contained" fullWidth onClick={handleOpen}>
        {t('save')}
      </Button>
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="xs"
        fullWidth
        fullScreen={Device.isMobileOnly}
        scroll="body"
      >
        <Stack spacing={10}>
          <DialogTitle>{t('confirmDialog.title.Confirm edit', { ns: 'booking' })}</DialogTitle>
          <DialogContent>
            <Stack spacing={2} width="100%" divider={<Divider />}>
              <Stack justifyContent="space-between" width="100%" sx={{ flexDirection: 'row' }}>
                <Typography variant="body2">{t('dataGrid.Date', { ns: 'booking' })}</Typography>
                <Typography variant="body2">{startTime.format('dddd LL')}</Typography>
              </Stack>
              <Stack justifyContent="space-between" width="100%" sx={{ flexDirection: 'row' }}>
                <Typography variant="body2">{t('dataGrid.Hour', { ns: 'booking' })}</Typography>
                <Typography variant="body2">{startTime.format('HH:mm')}</Typography>
              </Stack>
              <Stack justifyContent="space-between" width="100%" sx={{ flexDirection: 'row' }}>
                <Typography variant="body2">{t('dataGrid.Participants', { ns: 'booking' })}</Typography>
                <Typography variant="body2">{participants}</Typography>
              </Stack>
              <Stack justifyContent="space-between" width="100%" sx={{ flexDirection: 'row' }}>
                <Typography variant="body2">{t('dataGrid.Occasion', { ns: 'booking' })}</Typography>
                <Typography variant="body2">
                  {occasion ? t(`occasion.${occasion}`, { ns: 'booking' }) : t(`word.null`, { ns: 'common' })}
                </Typography>
              </Stack>
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button variant="contained" color="uncolored" onClick={handleClose}>
              {t('cancel')}
            </Button>
            <Button variant="contained" color="uncolored" onClick={handleClickConfirm}>
              {t('confirm')}
            </Button>
          </DialogActions>
        </Stack>
      </Dialog>
    </>
  );
}
